import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Plus, Calendar, Check, X as XIcon, Clock, AlertTriangle } from 'lucide-react';

const STATUS_COLORS = {
  scheduled: 'bg-blue-500/10 text-blue-400',
  completed: 'bg-green-500/10 text-green-400',
  cancelled: 'bg-red-500/10 text-red-400',
  no_show: 'bg-yellow-500/10 text-yellow-400',
};

export default function Meetings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const [meetings, setMeetings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ lead_id: '', property_id: '', meeting_date: '', notes: '', status: 'scheduled' });
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const [meetRes, leadRes, propRes] = await Promise.all([
        api.get(`/api/meetings?${params}`),
        api.get('/api/leads?limit=200'),
        api.get('/api/properties?limit=200')
      ]);
      setMeetings(meetRes.data.meetings);
      setTotal(meetRes.data.total);
      setLeads(leadRes.data.leads);
      setProperties(propRes.data.properties);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/meetings', form);
      setShowForm(false); setForm({ lead_id: '', property_id: '', meeting_date: '', notes: '', status: 'scheduled' }); load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const updateStatus = async (id, status) => {
    try { await api.put(`/api/meetings/${id}`, { status }); load(); }
    catch (err) { alert('Error updating meeting'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div><h3 className="text-xl font-bold text-white">Meetings ({total})</h3><p className="text-dark-400 text-sm">Track client meetings</p></div>
        <div className="flex gap-2">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
            <option value="">All</option><option value="scheduled">Scheduled</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option><option value="no_show">No Show</option>
          </select>
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-dark-900 font-medium rounded-lg text-sm"><Plus className="w-4 h-4" />New Meeting</button>
        </div>
      </div>

      {loading ? <div className="flex justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div></div> :
      meetings.length === 0 ? <div className="glass rounded-xl p-8 text-center"><Calendar className="w-12 h-12 text-dark-500 mx-auto mb-3" /><p className="text-dark-400">No meetings found</p></div> :
      <div className="space-y-3">
        {meetings.map(m => (
          <div key={m.id} className="glass rounded-xl p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-white font-medium">{m.lead_name || 'Lead'}</h4>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[m.status] || ''}`}>{m.status}</span>
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-dark-400">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{m.meeting_date}</span>
                  <span>Agent: {m.agent_name}</span>
                  {m.lead_phone && <span>{m.lead_phone}</span>}
                  {m.notes && <span className="text-dark-500">{m.notes}</span>}
                </div>
              </div>
              {m.status === 'scheduled' && (
                <div className="flex gap-2">
                  <button onClick={() => updateStatus(m.id, 'completed')} className="px-3 py-1.5 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded text-xs flex items-center gap-1"><Check className="w-3 h-3" />Done</button>
                  <button onClick={() => updateStatus(m.id, 'no_show')} className="px-3 py-1.5 bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 rounded text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" />No Show</button>
                  <button onClick={() => updateStatus(m.id, 'cancelled')} className="px-3 py-1.5 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded text-xs flex items-center gap-1"><XIcon className="w-3 h-3" />Cancel</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>}

      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-dark-900 border border-dark-700 rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-dark-700">
              <h3 className="text-lg font-semibold text-white">New Meeting</h3>
              <button onClick={() => setShowForm(false)} className="p-1 text-dark-400 hover:text-white"><XIcon className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div><label className="block text-sm text-dark-300 mb-1">Lead *</label><select value={form.lead_id} onChange={e => setForm({...form, lead_id: e.target.value})} required className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm"><option value="">Select Lead</option>{leads.map(l => <option key={l.id} value={l.id}>{l.name} {l.phone ? `(${l.phone})` : ''}</option>)}</select></div>
              <div><label className="block text-sm text-dark-300 mb-1">Property</label><select value={form.property_id} onChange={e => setForm({...form, property_id: e.target.value})} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm"><option value="">Select Property</option>{properties.filter(p => p.status === 'available').map(p => <option key={p.id} value={p.id}>{p.name} - AED {p.value?.toLocaleString()}</option>)}</select></div>
              <div><label className="block text-sm text-dark-300 mb-1">Date *</label><input type="date" value={form.meeting_date} onChange={e => setForm({...form, meeting_date: e.target.value})} required className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm" /></div>
              <div><label className="block text-sm text-dark-300 mb-1">Notes</label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm resize-none h-16" /></div>
              <div className="flex gap-3"><button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 bg-dark-800 text-dark-300 rounded-lg text-sm">Cancel</button><button type="submit" className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-dark-900 rounded-lg text-sm font-medium">Create Meeting</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
